import { useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import { isTelcoUser } from 'src/utils/auth-utils/checkTelcoUser';
import {
  getLocalStorage,
  setLocalStorage
} from 'src/utils/browserStorageHandler';
import { DynamicObject } from 'src/utils/commonTypes';
import {
  NEW_NFT_MENU_ITEM_FLAG,
  USER_TOKEN,
  USER_TYPE
} from 'src/utils/constants/localStorageConstants';
import {
  HamburgerMenuLinks,
  NavMenuLinks,
  ProfileMenuLinks
} from 'src/utils/constants/RouterConstants';
import { CountryCode, UserTypes } from 'src/utils/constants/stringConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { ONE_TRUST_COOKIE_CONTRIES } from 'src/utils/oneTrustHelper';
import { getFeatureFlagConfig, isFeatureEnabled } from 'src/utils/RemoteConfig';

import { useAppStore } from 'src/hooks/useAppStore';
import { colors } from '../../styles/colors';
import { HAMBURGERMENU } from '../../utils/constants/HeaderConstants';
import { getProfileMenuList } from '../profileMenu/ProfileMenuHelper';
import HamburgerMenuGroup, {
  ItemsType
} from './hamburgerMenuGroup/HamburgerMenuGroup';
import {
  activeItem,
  HambugerMenuContainer,
  HambugerMenuContainerMobile,
  HamburgerMenuFooterTitle,
  HamburgerMenuHeaderTitle
} from './HamburgerMenuStyle';
import { globalUrlHelper } from '../../utils/common';

const HamburgerMenuHeader = styled.div`
  text-align: left;
  cursor: pointer;
  padding: 15.5px 0px;
`;

const HamburgerMenuFooter = styled.div`
  text-align: center;
  padding: 22px 0px 22px;
  border-top: 2px solid ${colors.PrimaryWhiteColorAlpha10};
`;
const container = (isMobile: boolean) => css`
  position: relative;
  border-radius: 4px;
  ${!isMobile && 'right:20px;'}
`;

//Hamburger menu props
type HamburgerMenuProps = {
  isMobile: boolean;
  showHamburgerMenu: boolean;
  hamburgerMenuCLickHandler: () => void;
};

const HamburgerMenu = ({
  isMobile,
  showHamburgerMenu,
  hamburgerMenuCLickHandler
}: HamburgerMenuProps) => {
  const {
    initialLaunchData,
    translations,
    remoteConfig,
    usersLocationData,
    isUserLoggedIn,
    subscriptionData,
    bestOfferingPlan
  } = useAppStore();
  const headerStrings = translations?.common;
  const userType = getLocalStorage(USER_TYPE) || UserTypes.GUEST;

  const router = useRouter();

  const currentPath = router.asPath;
  const shouldShowStudentDiscount =
    initialLaunchData?.country_config?.student_discount;

  const referralFeatureFlag =
    isFeatureEnabled(remoteConfig, FeatureFlagConstants.CONFIG_REFERRAL) ||
    false;

  const showBlogLinkIndia =
    isFeatureEnabled(remoteConfig, FeatureFlagConstants.FEATURE_BLOG_LINK) ||
    false;

  const showBlogLinkGlobal =
    isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.FEATURE_BLOG_LINK_GLOBAL
    ) || false;

  const showNFTOption =
    isFeatureEnabled(remoteConfig, FeatureFlagConstants.FEATURE_NFT_FLAG) ||
    false;

  const gamificationConfig = getFeatureFlagConfig(
    remoteConfig,
    FeatureFlagConstants.ALL_FEATURE_LIVE_GAMIFICATION_CONFIG
  );

  const token = getLocalStorage(USER_TOKEN);
  let currentActiveSubscriptions = [];
  if (subscriptionData) {
    currentActiveSubscriptions = subscriptionData.currentActiveSubscriptions;
  }

  const shouldShowUsReferral =
    initialLaunchData &&
    initialLaunchData.country_config &&
    initialLaunchData.country_config.us_referral;

  let collectionSequence: DynamicObject = {};
  if (initialLaunchData?.country_config?.menu_options) {
    collectionSequence = initialLaunchData?.country_config?.menu_options.map(
      (menuOption) => menuOption?.name || ''
    );
  }
  const homeData = collectionSequence;
  const HeaderStrings = translations?.common;
  const countryCode = usersLocationData?.country_code || CountryCode.IN;
  const isGlobal = countryCode !== CountryCode.IN;

  const isGlobalActivePlan =
    isGlobal && isUserLoggedIn && !currentActiveSubscriptions.length;
  if (showNFTOption && !getLocalStorage(NEW_NFT_MENU_ITEM_FLAG))
    setLocalStorage(NEW_NFT_MENU_ITEM_FLAG, true);

  //Formatted data for the hamburger menu
  const formattedData = [];
  if (homeData) {
    for (const data in Object.keys(homeData)) {
      if (
        homeData[data] != HAMBURGERMENU.HOME_KEY &&
        HAMBURGERMENU[homeData[data]]
      ) {
        formattedData.push({
          name: HeaderStrings[HAMBURGERMENU[homeData[data]]],
          link: NavMenuLinks[
            HAMBURGERMENU[homeData[data]] as keyof typeof NavMenuLinks
          ],
          isActive: false
        });
      }
    }
  }

  const exploreData = formattedData.map((item) => {
    return {
      ...item,
      isActive: currentPath ? currentPath.indexOf(item.link) >= 0 : false
    };
  });

  const getBuyPlanText = () => {
    if (countryCode == CountryCode.IN) {
      return HeaderStrings.BUY_PLAN;
    }

    return bestOfferingPlan && userType === UserTypes.GUEST
      ? `${headerStrings?.SUBSCRIBE} @ ${bestOfferingPlan?.actual_price} ${bestOfferingPlan?.currency}`
      : HeaderStrings.BUY_PLAN;
  };

  //Plans data
  const plansData = [
    {
      name: getBuyPlanText(),
      link: HamburgerMenuLinks.BUY_PLAN,
      isActive: false
    },
    {
      name: HeaderStrings.HAVE_A_PREPAID_CODE,
      link: HamburgerMenuLinks.BUY_PLAN,
      isActive: false
    },
    shouldShowStudentDiscount
      ? {
          name: HeaderStrings.STUDENT_DISCOUNT,
          link: HamburgerMenuLinks.STUDENT,
          isActive: false
        }
      : undefined,
    shouldShowUsReferral
      ? {
          name: HeaderStrings.USREFERRAL_INVITE,
          link: ProfileMenuLinks.USREFERRAL,
          isActive: false
        }
      : undefined
  ];

  //Settings data
  const settingsData: ItemsType[] = [
    {
      name: HeaderStrings.RESET_SETTINGS,
      link: HamburgerMenuLinks.RESET_SETTINGS,
      isActive: false
    },
    {
      name: HeaderStrings.FAQ,
      link: HamburgerMenuLinks.FAQ,
      external: true,
      isActive: false
    },
    ONE_TRUST_COOKIE_CONTRIES.includes(countryCode)
      ? {
          name: HeaderStrings.CONSENT_SETTINGS,
          link: HamburgerMenuLinks.CONSENT_SETTINGS,
          isActive: false
        }
      : undefined
  ];

  if (token) {
    const index = settingsData.findIndex(
      (setting) => setting?.link === HamburgerMenuLinks.PARENTAL_CONTROL
    );
    if (index === -1) {
      settingsData.unshift({
        name: HeaderStrings.PARENTAL_CONTROL.PARENTAL_CONTROL,
        link: HamburgerMenuLinks.PARENTAL_CONTROL,
        isActive: false
      });
    }

    const activeTVOption = {
      name: HeaderStrings.AUTH_DEVICE,
      link: HamburgerMenuLinks.AUTH_DEVICE,
      isActive: false
    };

    settingsData.splice(1, 0, activeTVOption);
  }

  if (isMobile) {
    settingsData.splice(0, 0, {
      name: HeaderStrings.LANGUAGE,
      link: HamburgerMenuLinks.LANGUAGE,
      isActive: false
    });
  }

  if (
    gamificationConfig &&
    gamificationConfig?.enabled &&
    gamificationConfig?.payload?.settings_config?.is_entry_point_enabled
  ) {
    settingsData.push({
      name: 'Jalwa Dikhao Dubai Jao',
      link: globalUrlHelper(
        `${HamburgerMenuLinks.GAMIFICATION_ROUTE}?game_mapper_id=${gamificationConfig?.payload?.settings_config?.game_mapper_id}&settings_entry=true`
      ),
      isActive: false
    });
  }

  let showBlogLink = false;

  if (
    (countryCode === 'IN' && showBlogLinkIndia) ||
    (countryCode !== 'IN' && showBlogLinkGlobal)
  ) {
    showBlogLink = true;
  } else {
    showBlogLink = false;
  }

  //info data
  const infoData = [
    {
      name: HeaderStrings.ABOUTUS,
      link: HamburgerMenuLinks.ABOUT_US,
      isActive: false
    },
    {
      name: HeaderStrings.TERMS,
      link: HamburgerMenuLinks.TERMS,
      isActive: false
    },
    {
      name: HeaderStrings.PRIVACY_POLICY,
      link: HamburgerMenuLinks.POLICY,
      isActive: false
    }
  ];

  if (!isGlobal) {
    const contentRedressalOption = {
      name: HeaderStrings.CONTENT_REDRESSAL,
      link: HamburgerMenuLinks.CONTENT_REDRESSAL_MECHANISM,
      external: true,
      isActive: false
    };
    infoData.splice(2, 0, contentRedressalOption);
  }

  if (showNFTOption && !isGlobal) {
    const nftOption = {
      name: HeaderStrings.MY_NFT,
      link: HamburgerMenuLinks.NFT,
      isActive: false
    };
    infoData.splice(1, 0, nftOption);
  }

  if (showBlogLink) {
    const blogOption = {
      name: HeaderStrings.BLOG,
      link: HamburgerMenuLinks.BLOG,
      isActive: false
    };
    infoData.push(blogOption);
  }

  // refer and earn data
  const referralData = [
    {
      name: HeaderStrings.Referral_MoreMenu_Refer_Text,
      link: HamburgerMenuLinks.REFERRAL_ROUTE,
      isActive: false
    }
  ];

  //Menu data
  const menuData = [
    {
      name: HeaderStrings.EXPLORE,
      show: false,
      items: exploreData
    },
    {
      name: HeaderStrings.SETTINGS,
      show: true,
      items: settingsData
    },
    !isUserLoggedIn || isTelcoUser() || isGlobalActivePlan
      ? {
          name: HeaderStrings.MYPLANS,
          show: true,
          items: plansData
        }
      : undefined,
    referralFeatureFlag
      ? {
          name: '',
          show: true,
          items: referralData
        }
      : undefined,
    {
      name: HeaderStrings.INFO,
      show: true,
      items: infoData
    }
  ];

  if (isMobile) {
    const HeaderStrings = translations?.common;

    const { payload } = getFeatureFlagConfig(
      remoteConfig,
      FeatureFlagConstants.CONFIG_SPORTS
    ) || { predict_win_flag: false };
    const predictWinFlag = payload['predict_win_flag'] || false;

    const profileMenuList = initialLaunchData
      ? getProfileMenuList(
          HeaderStrings,
          initialLaunchData,
          isUserLoggedIn,
          predictWinFlag
        )
      : [];

    const myAccountOption = {
      name: HeaderStrings.MYACCOUNT,
      show: false,
      items: profileMenuList.filter((item) => {
        if (item) return item;
      })
    };

    if (isUserLoggedIn) {
      menuData.splice(2, 0, myAccountOption);
    }
  }

  // State Hooks
  const [menuState, setMenuState] = useState(menuData);

  const handleMenuGroupCollapse = (name: string) => {
    setMenuState((prevState) => {
      return prevState.map((element) => {
        if (element?.name === name) {
          return { ...element, show: !element.show };
        }
        return element;
      });
    });
  };

  const homeClickHandler = () => {
    window.location.href = NavMenuLinks.HOME;
  };

  const hamburgerBodyClickHandler = (e: React.MouseEvent<HTMLDivElement>) =>
    e.stopPropagation();

  return (
    <div css={container(isMobile)} data-testid="hamburger-menu-container">
      {showHamburgerMenu && (
        <div
          css={isMobile ? HambugerMenuContainerMobile : HambugerMenuContainer}
          onClick={hamburgerBodyClickHandler}>
          <HamburgerMenuHeader>
            <div
              onClick={homeClickHandler}
              css={[
                HamburgerMenuHeaderTitle,
                currentPath === NavMenuLinks.HOME && activeItem
              ]}
              data-testid="hamburger-menu-header">
              <a href={NavMenuLinks.HOME}>{HeaderStrings.HOME}</a>
            </div>
          </HamburgerMenuHeader>

          {menuData.map((menuGroup) => {
            if (menuGroup) {
              return (
                <HamburgerMenuGroup
                  key={menuGroup.name}
                  show={menuGroup.show}
                  name={menuGroup.name}
                  items={menuGroup.items}
                  onClick={handleMenuGroupCollapse}
                  hamburgerMenuCLickHandler={hamburgerMenuCLickHandler}
                />
              );
            }
          })}
          <HamburgerMenuFooter data-testid="hamburger-menu-footer">
            <span css={HamburgerMenuFooterTitle}>
              {HeaderStrings.VERSION} {process.env.APP_VERSION}
            </span>
          </HamburgerMenuFooter>
        </div>
      )}
    </div>
  );
};

export default HamburgerMenu;
